import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import pistolsImg from "../media3/header/pistol.png";
import riflesImg from "../media3/header/rifle.png";
import midtierImg from "../media3/header/mid.png";
import knivesImg from "../media3/header/knifes.png";
import glovesImg from "../media3/header/gloves.png";
import casesImg from "../media3/header/cases.png";
import collectionsImg from "../media3/header/collection.png";
import stickersImg from "../media3/header/sticker.png";
import allcat from "../media3/categories/s3.png";
import agentsImg from "../media3/header/agent.png";
import "../style/component/filter.css";

/* ------------------ Category Data ------------------ */
const categories = [
  { name: "All Categories", img: allcat, path: "/categories" },
  { name: "Pistols", img: pistolsImg, path: "/categories/pistols" },
  { name: "Rifle", img: riflesImg, path: "/categories/rifle" },
  { name: "Mid-Tier", img: midtierImg, path: "/categories/mid-tier" },
  { name: "Knives", img: knivesImg, path: "/categories/knives" },
  { name: "Gloves", img: glovesImg, path: "/categories/gloves" },
  { name: "Cases", img: casesImg, path: "/categories/cases" },
  { name: "Collections", img: collectionsImg, path: "/categories/collections" },
  { name: "Stickers", img: stickersImg, path: "/categories/stickers" },
  { name: "Agents", img: agentsImg, path: "/categories/agents" },
];

/* ------------------ Helper: URL → Category ------------------ */
const pathToCategoryName = (pathname: string) => {
  const parts = pathname.split("/").filter(Boolean);
  const idx = parts.indexOf("categories");
  const seg = idx !== -1 ? parts[idx + 1] : null;

  const map: Record<string, string> = {
    pistols: "Pistols",
    rifle: "Rifle",
    "mid-tier": "Mid-Tier",
    knives: "Knives",
    gloves: "Gloves",
    cases: "Cases",
    collections: "Collections",
    stickers: "Stickers",
    agents: "Agents",
  };

  return map[seg || ""] || "All Categories";
};

/* ------------------ SVG Arrow (Reusable) ------------------ */
const ArrowIcon: React.FC<{ open: boolean }> = ({ open }) =>
  open ? (
    <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#000">
      <path d="M480-360 280-560h400L480-360Z" />
    </svg>
  ) : (
    <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#000">
      <path d="M400-280v-400l200 200-200 200Z" />
    </svg>
  );

/* ------------------ Component ------------------ */
interface FilterProps {
  openFilter: string | null;
  toggleSection: (section: string) => void;
  priceRange: [number, number];
  handlePriceChange: (e: React.ChangeEvent<HTMLInputElement>, idx: 0 | 1) => void;
  selectedColors: string[];
  handleColorChange: (color: string) => void;
  colors: string[];
  selectedCategory?: string;
  setSelectedCategory?: (cat: string) => void;
  selectedRarity: string;
  handleRarityChange?: (rarity: string) => void;
  selectedOrderBy: string;
  handleOrderByChange?: (order: string) => void;
  onClearFilters?: () => void;
}

const Filter: React.FC<FilterProps> = ({
  openFilter,
  toggleSection,
  priceRange,
  handlePriceChange,
  selectedColors,
  handleColorChange,
  colors,
  selectedCategory,
  setSelectedCategory,
  selectedRarity,
  handleRarityChange,
  selectedOrderBy,
  handleOrderByChange,
  onClearFilters,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);
  const [drawerOpen, setDrawerOpen] = useState(false);

  /* ---- Resize listener ---- */
  useEffect(() => {
    const update = () => setIsMobile(window.innerWidth <= 1024);
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  const activeCategory = selectedCategory || pathToCategoryName(location.pathname);

  const handleCategoryClick = (cat: typeof categories[0]) => {
    setSelectedCategory?.(cat.name);
    navigate(cat.path);
    window.scrollTo({ top: 0, behavior: "auto" });
    if (isMobile) setDrawerOpen(false);
  };

  /* ------------------ Shared Filter UI ------------------ */
  const FilterContent = () => (
    <>
      <div className="sidebar-header-wrapper">
        <h2 className="sidebar-header">Filters <i className="icon-filter"></i></h2>
        {/* <button className="clear-filter-btn" onClick={onClearFilters}>
          Clear Filter
        </button> */}
      </div>

      {/* --- Categories --- */}
      <div className="filter-group">
        <div className="filter-title" onClick={() => toggleSection("Categories")}>
          Categories <ArrowIcon open={openFilter === "Categories"} />
        </div>

        {openFilter === "Categories" && (
          <div className="category-list">
            {categories.map((cat) => (
              <div
                key={cat.name}
                className={`category-item ${activeCategory === cat.name ? "selected" : ""}`}
                onClick={() => handleCategoryClick(cat)}
              >
                <img src={cat.img} alt={cat.name} width={24} height={24} />
                <span className="category-name">{cat.name}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* --- Price --- */}
      <div className="filter-group">
        <div className="filter-title" onClick={() => toggleSection("Price")}>
          Price <ArrowIcon open={openFilter === "Price"} />
        </div>

        {openFilter === "Price" && (
          <div className="price-slider">
            <input
              type="range"
              min="50"
              max="200"
              value={priceRange[0]}
              onChange={(e) => handlePriceChange(e, 0)}
            />
            <div className="price-values">
              <span>${priceRange[0]}</span>
              <span>${priceRange[1]}</span>
            </div>
          </div>
        )}
      </div>

      {/* --- Colors --- */}
      <div className="filter-group">
        <div className="filter-title" onClick={() => toggleSection("Colors")}>
          Colors <ArrowIcon open={openFilter === "Colors"} />
        </div>

        {openFilter === "Colors" && (
          <div className="color-options">
            {colors.map((c) => (
              <span
                key={c}
                className={`color-dot ${c} ${selectedColors.includes(c) ? "selected" : ""}`}
                onClick={() => handleColorChange(c)}
              />
            ))}
          </div>
        )}
      </div>

      {/* --- Rarity --- */}
      <div className="filter-group">
        <div className="filter-title" onClick={() => toggleSection("Rarity")}>
          Rarity <ArrowIcon open={openFilter === "Rarity"} />
        </div>

        {openFilter === "Rarity" && (
          <div className="rarity-options">
            {[
              "Any",
              // "Consume Grade",
              // "Industrial grade",
              
              // "Remarkable",
              "Restricted",
              "Classified",
              "Covert",
            ].map((r) => (
              <span
                key={r}
                className={`rarity-item ${selectedRarity === r ? "selected" : ""}`}
                onClick={() => handleRarityChange?.(r)}
              >
                {r}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* --- Order By --- */}
      <div className="filter-group">
        <div className="filter-title" onClick={() => toggleSection("OrderedBy")}>
          Ordered By <ArrowIcon open={openFilter === "OrderedBy"} />
        </div>

        {openFilter === "OrderedBy" && (
          <div className="ordered-options">
            {["Popularity", "Price", "Rating", "Newest"].map((o) => (
              <span
                key={o}
                className={`ordered-item ${selectedOrderBy === o ? "selected" : ""}`}
                onClick={() => handleOrderByChange?.(o)}
              >
                {o}
              </span>
            ))}
          </div>
        )}
      </div>
    </>
  );

  /* ------------------ Layout ------------------ */
  if (!isMobile) return <aside className="sidebar"><FilterContent /></aside>;

  return (
    <>
      <button className="mobile-filter-btn" onClick={() => setDrawerOpen(true)} aria-label="Open filters">
        <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#FFFFFF">
          <path d="M440-120v-240h80v80h320v80H520v80h-80Zm-320-80v-80h240v80H120Zm160-160v-80H120v-80h160v-80h80v240h-80Zm160-80v-80h400v80H440Zm160-160v-240h80v80h160v80H680v80h-80Zm-480-80v-80h400v80H120Z"/>
        </svg>
      </button>

      {drawerOpen && (
        <div className="mobile-filter-overlay" onClick={() => setDrawerOpen(false)}>
          <aside className="mobile-filter-drawer" onClick={(e) => e.stopPropagation()}>
            <button className="mobile-filter-close" onClick={() => setDrawerOpen(false)} aria-label="Close filters">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6L6 18" /><path d="M6 6l12 12" />
              </svg>
            </button>
            <FilterContent />
          </aside>
        </div>
      )}
    </>
  );
};

export default Filter;